import { get, set, del, keys } from "idb-keyval";

// Phase F3 — durable offline submission queue.
//
// Uses idb-keyval (a thin Promise-based wrapper around IndexedDB) so each
// queued record survives page reloads, browser restarts, and even crashes
// mid-submission. We deliberately do NOT use localStorage because (a) it's
// synchronous + small, and (b) it can't store Blobs natively — the player's
// photo would have to be base64-encoded, tripling its memory footprint.
//
// Queue records are addressed by their clientSubmissionId (the SAME UUID
// the server sees as the idempotency key). That choice means a successful
// background drain can `removeQueued(record.id)` even if the server's
// response said "already processed" — both sides agree on the key.
//
// All operations are async + best-effort: any IndexedDB failure is logged
// and the offline UX silently degrades to "your phone can't save right
// now" rather than throwing into the React tree.

const QUEUE_KEY_PREFIX = "jingle:offline-find:";

// Custom DOM event fired whenever the queue's contents change (enqueue,
// remove, or attempt-bump). The Phase F3 sync coordinator (singleton in
// <OfflineSyncProvider>) listens for it and refreshes its pendingCount
// state, which keeps the header badge / "Sync now" chip honest WITHOUT
// requiring every caller to thread a refresh function through props.
//
// We use a CustomEvent on `window` rather than a module-level event
// emitter so the listener pattern survives HMR remounts and multiple
// React roots in tests.
export const OFFLINE_QUEUE_EVENT = "jingle:offline-queue-changed";

function emitQueueChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT));
  } catch {
    // CustomEvent unavailable (very old browsers / SSR shims) — the
    // hook also re-checks on `online` events + isOnline flips, so the
    // badge will recover within a tick.
  }
}

export interface OfflineFindRecord {
  id: string; // clientSubmissionId — also the idb key suffix
  huntId: number;
  fileBlob: Blob;
  fileName: string;
  fileType: string;
  roomName?: string;
  lat?: number;
  lng?: number;
  createdAt: number; // ms epoch
  attempts: number; // bumped each drain attempt, success or failure
  lastError?: string; // short string for UI surfacing; never includes blob
}

function fullKey(id: string): string {
  return `${QUEUE_KEY_PREFIX}${id}`;
}

export async function enqueueOfflineFind(
  record: Omit<OfflineFindRecord, "createdAt" | "attempts">,
): Promise<OfflineFindRecord> {
  const stored: OfflineFindRecord = {
    ...record,
    createdAt: Date.now(),
    attempts: 0,
  };
  await set(fullKey(stored.id), stored);
  emitQueueChanged();
  return stored;
}

export async function listQueuedOfflineFinds(): Promise<OfflineFindRecord[]> {
  // Filter to JINGLE keys only — other apps in the same origin (or future
  // jingle features) may also use idb-keyval and we don't want to leak.
  const all = await keys();
  const ours = all.filter(
    (k): k is string =>
      typeof k === "string" && k.startsWith(QUEUE_KEY_PREFIX),
  );
  const records = await Promise.all(
    ours.map(async (k) => (await get(k)) as OfflineFindRecord | undefined),
  );
  return records
    .filter((r): r is OfflineFindRecord => r !== undefined)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeQueuedOfflineFind(id: string): Promise<void> {
  await del(fullKey(id));
  emitQueueChanged();
}

export async function markQueuedAttempt(
  id: string,
  error?: string,
): Promise<void> {
  const existing = (await get(fullKey(id))) as OfflineFindRecord | undefined;
  if (!existing) return;
  const updated: OfflineFindRecord = {
    ...existing,
    attempts: existing.attempts + 1,
    lastError: error,
  };
  await set(fullKey(id), updated);
  emitQueueChanged();
}
